import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Button,
  Snackbar,
  Alert,
  Typography,
  Box,
  InputAdornment,
  Avatar,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
} from "@mui/material";
import {
  Lock,
  Person,
  VpnKey,
  CheckCircle,
  Security,
} from "@mui/icons-material";
import { API_BASE_URL } from "../constant/constant";

const TwoFactorAuth = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const otpRef = useRef(null);
  const otpSubmitted = useRef(false);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [qr, setQr] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [is2FASetup, setIs2FASetup] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackSeverity, setSnackSeverity] = useState("info");
  const [message, setMessage] = useState("");

  /* ================= MOBILE-SAFE STYLES ================= */

  const mobileTextFieldSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: isMobile ? 44 : 56,
      alignItems: "center",
    },
    "& .MuiOutlinedInput-input": {
      padding: isMobile ? "10px 12px" : "16.5px 14px",
      fontSize: isMobile ? "0.95rem" : "1rem",
      lineHeight: 1.2,
    },
    "& .MuiInputAdornment-root": {
      height: "100%",
      alignItems: "center",
    },
    "& .MuiSvgIcon-root": {
      fontSize: isMobile ? "1.1rem" : "1.3rem",
    },
  };

  const mobileButtonSx = {
    minHeight: isMobile ? 44 : 52,
    fontSize: isMobile ? "0.95rem" : "1rem",
    fontWeight: 600,
    borderRadius: 2,
  };

  /* ================= HELPERS ================= */

  const showMessage = (msg, severity = "info") => {
    setMessage(msg);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  /* ================= LOGIN ================= */

  const handleLogin = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/2fa/login`, {
        username,
        password,
      });

      setIsAuthenticated(true);

      if (res.data.secret) {
        setIs2FASetup(true);
      } else {
        const setupRes = await axios.get(
          `${API_BASE_URL}/2fa/setup/${username}`
        );
        setQr(setupRes.data.qr);
        setIs2FASetup(false);
      }
    } catch (err) {
      showMessage(err.response?.data?.message || "Login failed", "error");
    }
  };

  /* ================= OTP VERIFY ================= */

  const verifyOTP = async () => {
    if (otp.length !== 6 || otpSubmitted.current) return;

    otpSubmitted.current = true;

    try {
      const res = await axios.post(`${API_BASE_URL}/2fa/verify`, {
        username,
        token: otp,
      });

      showMessage(res.data.message, "success");

      localStorage.setItem("token", "true");
      localStorage.setItem("userrole", res.data.user.userrole);
      localStorage.setItem("userfullname", res.data.user.userfullname);
      localStorage.setItem("useremail", res.data.user.emaild);
      localStorage.setItem("username", res.data.user.uname);
      localStorage.setItem("isAuthenticated", "true");

      navigate("/dashboard");
    } catch (err) {
      otpSubmitted.current = false;
      showMessage(
        err.response?.data?.message || "OTP verification failed",
        "error"
      );
    }
  };

  /* ================= OTP AUTO-SUBMIT ================= */

  useEffect(() => {
    if (otp.length === 6) {
      verifyOTP();
    }
  }, [otp]);

  /* ================= AUTO-FOCUS OTP ================= */

  useEffect(() => {
    if (isAuthenticated && otpRef.current) {
      setTimeout(() => otpRef.current.focus(), 200);
    }
  }, [isAuthenticated]);

  /* ================= INIT ================= */

  useEffect(() => {
    const auth = localStorage.getItem("isAuthenticated") === "true";
    if (!auth) {
      localStorage.clear();
      localStorage.setItem("isAuthenticated", "false");
    }
  }, []);

  /* ================= UI ================= */

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg,#e3f2fd,#f8fafc)",
        p: 2,
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: "100%",
          maxWidth: 920,
          borderRadius: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* LEFT PANEL */}
        {!isMobile && (
          <Box
            sx={{
              width: "45%",
              background: "linear-gradient(160deg,#1976d2,#0d47a1)",
              color: "#fff",
              p: 5,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="h4" fontWeight={700} mb={2}>
              Welcome Back
            </Typography>
            <Typography fontSize={14}>
              Secure login with Two-Factor Authentication
            </Typography>
          </Box>
        )}

        {/* RIGHT PANEL */}
        <Box
  sx={{
    width: { xs: "90%", sm: "100%" },
    mx: "auto",
    p: { xs: 2, md: 5 },
  }}
>
          <Box textAlign="center" mb={2}>
            <Avatar sx={{ bgcolor: "primary.main", mx: "auto", mb: 1 }}>
              <Security />
            </Avatar>
            <Typography variant="h6" fontWeight={700}>
              Secure Login
            </Typography>
            <Typography fontSize={13} color="text.secondary">
              {isAuthenticated
                ? "Step 2 of 2 • Verify OTP"
                : "Step 1 of 2 • Login"}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* LOGIN */}
          <Fade in={!isAuthenticated} unmountOnExit>
            <Box display="flex" flexDirection="column" gap={1.4}>
              <TextField
                label="Username"
                fullWidth
                sx={mobileTextFieldSx}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    passwordRef.current?.focus();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Password"
                type="password"
                fullWidth
                sx={mobileTextFieldSx}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleLogin(); // moves to OTP only if login succeeds
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                variant="contained"
                startIcon={<VpnKey />}
                onClick={handleLogin}
                sx={mobileButtonSx}
              >
                SIGN IN
              </Button>
            </Box>
          </Fade>

          {/* OTP */}
          <Fade in={isAuthenticated} unmountOnExit>
            <Box display="flex" flexDirection="column" gap={1.4}>
              {!is2FASetup && qr && (
                <Box textAlign="center" p={2} bgcolor="#f5f7fb" borderRadius={2}>
                  <Typography fontWeight={600} mb={1}>
                    Scan QR Code
                  </Typography>
                  <img src={qr} width={isMobile ? 140 : 160} alt="QR" />
                </Box>
              )}

              <TextField
                label="6-Digit OTP"
                fullWidth
                sx={mobileTextFieldSx}
                inputRef={otpRef}
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, ""))
                }
                inputProps={{
                  inputMode: "numeric",
                  maxLength: 6,
                  style: {
                    textAlign: "center",
                    letterSpacing: isMobile ? "0.25rem" : "0.4rem",
                    fontWeight: 700,
                  },
                }}
              />

              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={verifyOTP}
                sx={mobileButtonSx}
              >
                VERIFY OTP
              </Button>
            </Box>
          </Fade>
        </Box>
      </Paper>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
      >
        <Alert
          severity={snackSeverity}
          variant="filled"
          onClose={() => setSnackOpen(false)}
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TwoFactorAuth;
